import { useEffect, useRef } from 'react';
import { useI18n } from './i18n';
import { ContextCloseBar, contextPanelPosition } from './ContextPanel';

/**
 * SegmentModal.js — compact, live-updating transition-property editor, shown
 * above the viewport controls (same position as PromptModal).
 *
 * Layout (compact, no save/cancel — every change applies immediately):
 *   ┌─────────────────────────────────────────────┐
 *   │                Close                        │
 *   │ Title: description text                    │
 *   │ Style:   [Straight|Curve L|Curve R]         │
 *   │ Curve:   ────────●───────────               │
 *   │ Arch:    ────●───────────────               │
 *   └─────────────────────────────────────────────┘
 *
 * controls:
 *   { type: 'choice', key, label, options: [{ key, label }] } — segmented buttons
 *   { type: 'slider', key, label, min, max, step }            — range input
 *     (sliders deliberately show NO numeric value)
 *   { type: 'action', label, onClick, danger? }              — one-tap command row
 *   { type: 'color', key, label, defaultHex? }               — colour + DEFAULT pill
 *
 * initial — map of control-key → starting value. onChange(values) fires on
 * every edit (live update). onClose() dismisses (red Close bar).
 */

const rowLabelStyle = {
  width: '64px', flexShrink: 0,
  fontSize: '10px', fontWeight: '800', color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'flex', alignItems: 'center',
};

export default function SegmentModal({
  title = 'Transition',
  description = '',
  controls = [],
  initial = {},
  onChange = null,
  onClose = null,
  bottom = 0, // distance from the bottom of the positioning context (timeline stack height)
}) {
  const { t } = useI18n();
  const values = { ...initial }; // live: the parent owns the truth; we just forward edits
  // Keyboard flow: focus the FIRST slider the moment the editor opens, so
  // ↑/↓ adjust it right away and Escape (App's guard lets range-input Escapes
  // through) closes it — open with Enter, tune with arrows, Esc, no Tab hunt.
  const firstSliderRef = useRef(null);
  const firstSliderIndex = controls.findIndex(c => c.type === 'slider');
  useEffect(() => {
    if (firstSliderRef.current) firstSliderRef.current.focus();
  }, []);

  // One compact row of related steppers (the pitch X/Z nudges) instead of four
  // stacked full-width buttons: the panel stays short enough to read at a glance.
  const actionBtn = (c, i) => (
    <button
      key={i}
      type="button"
      onClick={c.onClick}
      title={c.hint || undefined}
      style={{
        fontSize: '10px', fontWeight: '800', padding: '5px 8px', borderRadius: '4px',
        letterSpacing: '0.04em', cursor: 'pointer', whiteSpace: 'nowrap',
        background: c.danger ? 'rgba(239,68,68,0.16)' : '#1e293b',
        color: c.danger ? '#f87171' : '#cbd5e1',
        border: `1px solid ${c.danger ? 'rgba(239,68,68,0.45)' : '#334155'}`,
      }}
    >
      {c.label}
    </button>
  );

  return (
    <div
      className="kv-context-panel kv-segment-panel"
      style={{
        ...contextPanelPosition(bottom),
        zIndex: 250,
        background: 'rgba(15, 23, 42, 0.97)',
        border: '1px solid #a78bfa',
        borderRadius: '8px',
        boxShadow: '0 12px 34px rgba(0,0,0,0.65)',
        padding: 0,
        minWidth: 0, width: 'min(190px, calc(100vw - 16px))',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <ContextCloseBar label={t('fb.close')} onClick={onClose} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '12px', fontWeight: '800', color: '#f8fafc', whiteSpace: 'nowrap' }}>{title}:</span>
        {description && (
          <span style={{ fontSize: '10.5px', color: '#94a3b8', lineHeight: '1.3', flex: 1, minWidth: 0 }}>{description}</span>
        )}
      </div>

      {controls.map((c, i) => {
        if (c.type === 'choice') {
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={rowLabelStyle}>{c.label}</span>
              <div style={{ flex: 1, display: 'flex', gap: '3px' }}>
                {c.options.map(o => {
                  const on = values[c.key] === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => onChange && onChange({ ...values, [c.key]: o.key })}
                      style={{
                        flex: 1, fontSize: '10px', fontWeight: '700',
                        padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap',
                        background: on ? '#a78bfa' : '#1e293b',
                        color: on ? '#0f172a' : '#cbd5e1',
                        border: `1px solid ${on ? '#a78bfa' : '#334155'}`,
                        cursor: 'pointer',
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        if (c.type === 'slider') {
          return (
            // c.hint teaches the keyboard twin of THIS slider (title only — the
            // layout is unchanged), so the editor itself points at the faster
            // way instead of being the only way.
            <div key={i} title={c.hint || undefined} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={rowLabelStyle}>{c.label}</span>
              <input
                ref={i === firstSliderIndex ? firstSliderRef : undefined}
                type="range"
                min={c.min}
                max={c.max}
                step={c.step ?? 0.01}
                value={values[c.key] ?? c.min}
                onChange={(e) => onChange && onChange({ ...values, [c.key]: parseFloat(e.target.value) })}
                style={{ flex: 1, accentColor: '#a78bfa', cursor: 'pointer', height: '18px' }}
              />
            </div>
          );
        }
        if (c.type === 'color') {
          const currentHex = /^#[0-9a-fA-F]{6}$/.test(String(values[c.key] ?? ''))
            ? values[c.key]
            : (c.defaultHex || '#3b82f6');
          const isDefault = values[c.key] === 'default' || values[c.key] == null;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={rowLabelStyle}>{c.label}</span>
              <div style={{ flex: 1, display: 'flex', gap: '6px', alignItems: 'center' }}>
                {/* Current colour — click to open the native picker.
                    A <label>, not a <button>, so the coarse rule that grows this
                    panel's buttons cannot reach it: it carries its own class. */}
                <label
                  className="kv-colour-swatch"
                  title={t('seg.currentColour')}
                  style={{
                    position: 'relative', width: '24px', height: '24px', borderRadius: '50%',
                    background: currentHex,
                    border: '2px solid #475569', cursor: 'pointer', flexShrink: 0,
                    overflow: 'hidden',
                    boxShadow: '0 0 0 1px #0b1120',
                  }}
                >
                  <input
                    type="color"
                    value={currentHex}
                    onChange={(e) => onChange && onChange({ ...values, [c.key]: e.target.value })}
                    style={{ position: 'absolute', inset: '-10px', width: '44px', height: '44px', opacity: 0, cursor: 'pointer' }}
                  />
                </label>
                {/* Default colour button — sets the item's default (home/away/ball) */}
                <button
                  type="button"
                  title={t('seg.defaultColour')}
                  aria-label={t('seg.defaultColourAria')}
                  onClick={() => onChange && onChange({ ...values, [c.key]: 'default' })}
                  style={{
                    height: '24px', padding: '0 8px', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', gap: '4px',
                    fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em',
                    background: isDefault ? 'rgba(148,163,184,0.25)' : 'transparent',
                    color: isDefault ? '#e2e8f0' : '#94a3b8',
                    border: isDefault ? '1px solid #94a3b8' : '1px dashed #64748b',
                    cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                  DEFAULT
                </button>
              </div>
            </div>
          );
        }
        if (c.type === 'action') {
          // A one-tap command row (e.g. "Delete key") — danger actions get the
          // red treatment used by the other context panels.
          return actionBtn(c, i);
        }
        if (c.type === 'row') {
          // A cluster of related one-tap commands sharing one line.
          return (
            <div key={i} style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {(c.items || []).map((sub, j) => actionBtn(sub, j))}
            </div>
          );
        }
        return null;
      })}
      </div>
    </div>
  );
}
